export function isAnswerEmpty(answer) {
  if (answer === null || answer === undefined) return true;
  if (typeof answer === "string" && answer.trim() === "") return true;
  if (Array.isArray(answer) && answer.length === 0) return true;
  return false;
}

export function matchesResponse(answer, response) {
  if (Array.isArray(answer)) {
    return answer.some((value) => matchesResponse(value, response));
  }
  if (answer === response) {
    return true;
  }
  if (
    answer === undefined ||
    answer === null ||
    response === undefined ||
    response === null
  ) {
    return false;
  }
  return String(answer) === String(response);
}

function matchesRequiredResponse(answer, response) {
  if (response === null) {
    return isAnswerEmpty(answer);
  }
  if (typeof answer === "string" && typeof response === "string") {
    return answer.toLowerCase() === response.toLowerCase();
  }
  return matchesResponse(answer, response);
}

function getQuestionByIndex(questions, index) {
  return questions && questions[index] ? questions[index] : null;
}

export function getQuestionIndex(questions, question) {
  return questions ? questions.indexOf(question) : -1;
}

export function getQuestionBlock(question) {
  return Number((question && question.bn) || 1);
}

function getBlockRule(blockRules, blockNumber) {
  return blockRules && blockRules[blockNumber] ? blockRules[blockNumber] : {};
}

export function getBlockTitle(blockRules, blockNumber, fallback = null) {
  const rule = getBlockRule(blockRules, blockNumber);
  return rule.t ? rule.t : fallback;
}

function getBlockCondition(questions, blockRules, blockNumber) {
  const rule = getBlockRule(blockRules, blockNumber);
  if (!hasBlockCondition(rule)) {
    return null;
  }
  const index = Number(rule.si.index);
  const question = getQuestionByIndex(questions, index);
  if (!question) {
    return null;
  }
  return {
    index,
    question,
    response: rule.si.response,
  };
}

function hasBlockCondition(rule) {
  return !!(
    rule &&
    rule.si &&
    rule.si.index !== undefined &&
    rule.si.index !== null &&
    rule.si.index !== "" &&
    rule.si.response !== undefined &&
    rule.si.response !== null &&
    rule.si.response !== ""
  );
}

function conditionMatches(questions, condition) {
  if (!condition || condition.index === undefined || condition.index === null) {
    return false;
  }
  const question = getQuestionByIndex(questions, Number(condition.index));
  return question ? matchesResponse(question.a, condition.response) : false;
}

function questionConditionsMatch(questions, question) {
  if (question.si && !conditionMatches(questions, question.si)) {
    return false;
  }
  if (question.hi && conditionMatches(questions, question.hi)) {
    return false;
  }
  return true;
}

function isInternalBlockRule(questions, blockRules, blockNumber) {
  const condition = getBlockCondition(questions, blockRules, blockNumber);
  return condition
    ? getQuestionBlock(condition.question) === Number(blockNumber)
    : false;
}

function blockConditionMatches(questions, blockRules, blockNumber) {
  const condition = getBlockCondition(questions, blockRules, blockNumber);
  return condition
    ? matchesResponse(condition.question.a, condition.response)
    : true;
}

function isQuestionAvailableForCondition(
  questions,
  blockRules,
  question,
  visitedBlocks = []
) {
  const blockNumber = getQuestionBlock(question);
  if (!isBlockVisible(questions, blockRules, blockNumber, visitedBlocks)) {
    return false;
  }
  const condition = getBlockCondition(questions, blockRules, blockNumber);
  if (
    condition &&
    isInternalBlockRule(questions, blockRules, blockNumber) &&
    getQuestionIndex(questions, question) !== condition.index &&
    !blockConditionMatches(questions, blockRules, blockNumber)
  ) {
    return false;
  }
  return questionConditionsMatch(questions, question);
}

function isBlockVisible(
  questions,
  blockRules,
  blockNumber,
  visitedBlocks = []
) {
  const condition = getBlockCondition(questions, blockRules, blockNumber);
  if (!condition || isInternalBlockRule(questions, blockRules, blockNumber)) {
    return true;
  }
  const currentBlock = Number(blockNumber);
  const conditionBlock = getQuestionBlock(condition.question);
  if (conditionBlock >= currentBlock || visitedBlocks.includes(currentBlock)) {
    return true;
  }
  const nextVisitedBlocks = [...visitedBlocks, currentBlock];
  if (
    !isQuestionAvailableForCondition(
      questions,
      blockRules,
      condition.question,
      nextVisitedBlocks
    )
  ) {
    return false;
  }
  return matchesResponse(condition.question.a, condition.response);
}

export function isQuestionVisible(questions, blockRules, question) {
  const blockNumber = getQuestionBlock(question);
  if (!isBlockVisible(questions, blockRules, blockNumber)) {
    return false;
  }
  const condition = getBlockCondition(questions, blockRules, blockNumber);
  if (condition && isInternalBlockRule(questions, blockRules, blockNumber)) {
    if (getQuestionIndex(questions, question) === condition.index) {
      return true;
    }
    if (!blockConditionMatches(questions, blockRules, blockNumber)) {
      return false;
    }
  }
  return questionConditionsMatch(questions, question);
}

export function isQuestionRequired(
  questions,
  question,
  defaultRequired = true
) {
  if (question.ric && question.ric.index != null) {
    const relatedQuestion = getQuestionByIndex(
      questions,
      Number(question.ric.index)
    );
    if (!relatedQuestion) {
      return false;
    }
    return matchesRequiredResponse(relatedQuestion.a, question.ric.value);
  }
  return question.r === undefined ? defaultRequired : question.r === true;
}

export function createBlockGroups(questions, blockNumber) {
  const totalBlocks = Number(blockNumber || 1);
  const safeQuestions = questions || [];
  const groups = [];
  for (let x = 1; x <= totalBlocks; x++) {
    groups.push(
      safeQuestions.filter((question) => getQuestionBlock(question) === x)
    );
  }
  return groups;
}

export function getVisibleBlockGroups(questions, blockRules, blockGroups) {
  return (blockGroups || [])
    .map((blockQuestions, index) => ({
      block: index + 1,
      questions: blockQuestions,
    }))
    .filter((group) => isBlockVisible(questions, blockRules, group.block));
}

export function emptyAnswerForQuestion(question) {
  return question.cb || question.t === "select-multi" ? [] : null;
}

export function normalizeMultipleAnswer(question) {
  if (!question.cb && question.t !== "select-multi") {
    return question.a;
  }
  if (Array.isArray(question.a)) {
    return question.a;
  }
  return isAnswerEmpty(question.a) ? [] : [question.a];
}

export function shouldUseOptionButtons(question) {
  if (!Array.isArray(question.o)) {
    return true;
  }
  if (question.t === "select" || question.t === "select-multi") {
    return false;
  }
  if (question.t === "radio") {
    return true;
  }
  return question.o.length <= 9;
}
